"use client";

import {
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { cn } from "../lib/cn";

export interface MenuItem {
  readonly key: string;
  readonly label: string;
  readonly onSelect: () => void;
  /** A second line, such as an enterprise's role or a person's email. */
  readonly detail?: string;
  readonly danger?: boolean;
  /** The item that is current (the active enterprise), marked and not selectable. */
  readonly current?: boolean;
}

export interface MenuTriggerProps {
  readonly ref: RefObject<HTMLButtonElement | null>;
  readonly id: string;
  readonly "aria-haspopup": "menu";
  readonly "aria-expanded": boolean;
  readonly "aria-controls": string;
  readonly onClick: () => void;
  readonly onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
}

const PLACEMENT = {
  "bottom-start": "top-full start-0 mt-1",
  "bottom-end": "top-full end-0 mt-1",
  "right-start": "top-0 start-full ms-2",
  "right-end": "bottom-0 start-full ms-2",
} as const;

/**
 * A menu button (the ARIA menu button pattern): the trigger opens a list of actions, arrow keys move through them,
 * Escape closes it and returns focus, and Tab or a click outside closes it. The trigger is the caller's button,
 * drawn by `trigger` with the props it must spread.
 */
export function Menu({
  label,
  items,
  trigger,
  header,
  placement = "bottom-start",
  className,
}: {
  /** The menu's accessible name. */
  label: string;
  items: readonly MenuItem[];
  trigger: (props: MenuTriggerProps) => ReactNode;
  /** Shown above the items, such as the signed-in person's name. */
  header?: ReactNode;
  placement?: keyof typeof PLACEMENT;
  className?: string;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const wrapper = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const selectable = items.map((item, index) => (item.current ? -1 : index)).filter((index) => index >= 0);

  const focusItem = useCallback((index: number | undefined) => {
    if (index !== undefined) itemRefs.current[index]?.focus();
  }, []);
  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const outside = (event: MouseEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) close(false);
    };
    document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, [open, close]);

  const openAt = (which: "first" | "last") => {
    setOpen(true);
    requestAnimationFrame(() => focusItem(which === "first" ? selectable[0] : selectable.at(-1)));
  };

  const onTriggerKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openAt("first");
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      openAt("last");
    }
  };

  const onMenuKey = (event: KeyboardEvent<HTMLDivElement>) => {
    const at = selectable.indexOf(itemRefs.current.indexOf(document.activeElement as HTMLButtonElement));
    const move = (to: number) => {
      event.preventDefault();
      focusItem(selectable[(to + selectable.length) % selectable.length]);
    };
    if (event.key === "ArrowDown") move(at + 1);
    else if (event.key === "ArrowUp") move(at - 1);
    else if (event.key === "Home") move(0);
    else if (event.key === "End") move(selectable.length - 1);
    else if (event.key === "Escape") {
      event.preventDefault();
      close(true);
    } else if (event.key === "Tab") close(false);
  };

  return (
    <div ref={wrapper} className={cn("relative", className)}>
      {trigger({
        ref: triggerRef,
        id: `${id}-trigger`,
        "aria-haspopup": "menu",
        "aria-expanded": open,
        "aria-controls": `${id}-menu`,
        onClick: () => (open ? close(false) : openAt("first")),
        onKeyDown: onTriggerKey,
      })}
      {open && (
        <div
          id={`${id}-menu`}
          role="menu"
          aria-label={label}
          tabIndex={-1}
          onKeyDown={onMenuKey}
          className={cn(
            "absolute z-50 flex min-w-56 flex-col rounded-(--radius-control) border border-border bg-surface p-1 shadow-overlay",
            PLACEMENT[placement],
          )}
        >
          {header !== undefined && (
            <div className="mb-1 border-b border-border-subtle px-2.5 pt-1.5 pb-2">{header}</div>
          )}
          {items.map((item, index) => (
            <button
              key={item.key}
              ref={(element) => {
                itemRefs.current[index] = element;
              }}
              type="button"
              role="menuitem"
              tabIndex={-1}
              aria-current={item.current ? "true" : undefined}
              aria-disabled={item.current ? true : undefined}
              onClick={() => {
                if (item.current) return;
                close(true);
                item.onSelect();
              }}
              className={cn(
                "flex flex-col items-start rounded-(--radius-chip) px-2.5 py-1.5 text-start text-body focus-visible:-outline-offset-2",
                item.current
                  ? "cursor-default bg-brand-50 text-brand-900"
                  : item.danger
                    ? "text-tone-problem-fg hover:bg-tone-problem-bg focus:bg-tone-problem-bg"
                    : "text-ink hover:bg-muted focus:bg-muted",
              )}
            >
              <span className={item.current ? "font-medium" : undefined}>{item.label}</span>
              {item.detail !== undefined && (
                <span className="text-caption text-ink-muted">{item.detail}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
